import React, { useEffect, useState } from "react";
import { CheckCircle2, Loader2, ShieldAlert } from "lucide-react";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import { getReportMeta, createReport, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const STATE_FLOW = ["Reported", "Under Review", "Corrected / Removed / Rejected"];

// Reusable report form. Pass listing context (vehicleId, source) for listing reports.
export const ReportForm = ({ vehicleId = null, listingSource = null, onDone }) => {
  const { user } = useAuth();
  const type = vehicleId ? "listing" : "general";
  const [categories, setCategories] = useState([]);
  const [form, setForm] = useState({ category: "", message: "", reporter_email: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  useEffect(() => { getReportMeta().then((d) => setCategories(d.categories || [])).catch(() => {}); }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const needsEmail = !(user && user.email);
  const valid = form.category && form.message.trim() && (!needsEmail || form.reporter_email.trim());

  const submit = async () => {
    if (!valid) return;
    setBusy(true); setError(null);
    try {
      const payload = {
        type, category: form.category, message: form.message,
        vehicle_id: vehicleId, listing_source: listingSource,
        reporter_email: needsEmail ? form.reporter_email : undefined,
      };
      const res = await createReport(payload);
      setResult(res);
      if (onDone) onDone(res);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  if (result) {
    return (
      <div data-testid="report-success">
        <div className="flex items-center gap-2 mb-2">
          <CheckCircle2 className="h-5 w-5 text-emerald-600" />
          <h3 className="font-display text-lg font-semibold">Report received</h3>
        </div>
        <div className="inline-flex items-center gap-2 mb-3 px-3 py-1 rounded-full bg-white border border-emerald-200">
          <span className="text-xs text-muted-foreground">Status</span>
          <span className="font-mono text-xs font-semibold text-emerald-700" data-testid="report-status">
            {(result.report?.status || "reported").toUpperCase()}
          </span>
        </div>
        <p className="text-sm text-slate-700 leading-relaxed">{result.message}</p>
        <div className="flex flex-wrap items-center gap-2 mt-4 text-xs text-muted-foreground">
          {STATE_FLOW.map((s, i) => (
            <React.Fragment key={s}>
              <span className={i === 0 ? "font-medium text-emerald-700" : ""}>{s}</span>
              {i < STATE_FLOW.length - 1 && <span>→</span>}
            </React.Fragment>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="report-form">
      {vehicleId && (
        <div className="text-xs text-muted-foreground">
          Reporting listing <span className="font-mono">{vehicleId}</span>
          {listingSource ? ` · source ${listingSource}` : ""}
        </div>
      )}
      <div className="space-y-1.5">
        <Label className="overline">What's the issue?</Label>
        <Select value={form.category} onValueChange={(v) => set("category", v)}>
          <SelectTrigger data-testid="report-category"><SelectValue placeholder="Select a category" /></SelectTrigger>
          <SelectContent>
            {categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      {needsEmail && (
        <div className="space-y-1.5">
          <Label className="overline">Your email</Label>
          <Input type="email" value={form.reporter_email} onChange={(e) => set("reporter_email", e.target.value)}
            placeholder="so we can follow up" data-testid="report-email" />
        </div>
      )}
      <div className="space-y-1.5">
        <Label className="overline">Details</Label>
        <Textarea rows={4} value={form.message} onChange={(e) => set("message", e.target.value)}
          placeholder="Describe what looks wrong or suspicious." data-testid="report-message" />
      </div>
      <div className="flex items-start gap-2 rounded-md bg-amber-50 border border-amber-200 p-2.5">
        <ShieldAlert className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
        <p className="text-[11px] text-amber-900 leading-snug">
          Atlas will never ask you to send money to secure or purchase a vehicle. If someone requested
          payment in Atlas's name, do not pay — report it here.
        </p>
      </div>
      {error && <p className="text-sm text-red-600" data-testid="report-error">{error}</p>}
      <Button onClick={submit} disabled={!valid || busy} data-testid="report-submit"
        className="bg-primary hover:bg-atlas-blue text-white">
        {busy ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Submitting…</> : "Submit report"}
      </Button>
    </div>
  );
};
